/**
 * useMilestones — list earned milestones for a user, joined to the
 * lookup catalog so we have label/description/icon ready.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EarnedMilestone {
  key: string;
  label: string;
  description: string;
  icon: string;
  sort_order: number;
  earned_at: string;
}

interface MilestoneLookup {
  key: string;
  label: string;
  description: string;
  icon: string;
  sort_order: number;
}

interface Row {
  earned_at: string;
  milestone_key: string;
  // PostgREST returns the joined record as either an object or a single-element
  // array depending on relationship inference. Handle both.
  milestones: MilestoneLookup | MilestoneLookup[] | null;
}

export function useMilestones(userId?: string | null) {
  const [items, setItems] = useState<EarnedMilestone[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("member_milestones")
      .select("earned_at, milestone_key, milestones(key, label, description, icon, sort_order)")
      .eq("user_id", userId)
      .order("earned_at", { ascending: false });
    const rows = (data as unknown as Row[] | null) ?? [];
    const earned: EarnedMilestone[] = [];
    for (const r of rows) {
      const m = Array.isArray(r.milestones) ? r.milestones[0] : r.milestones;
      if (!m) continue;
      earned.push({
        key: m.key,
        label: m.label,
        description: m.description,
        icon: m.icon,
        sort_order: m.sort_order,
        earned_at: r.earned_at,
      });
    }
    earned.sort((a, b) => a.sort_order - b.sort_order);
    setItems(earned);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { items, loading, reload };
}
